import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useScroll,
  useTransform,
} from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MEDIA } from "../../data/content";
import { MaskedLine } from "../motion/MaskedLine";
import { Magnetic } from "../motion/Magnetic";

export const Hero = () => {
  const sectionRef = useRef(null);

  // Scroll parallax — image drifts, headline lifts as you scroll away
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);
  const headlineY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  // Mouse-driven 3D tilt on the visual frame
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const rotateX = useSpring(rx, { stiffness: 120, damping: 14, mass: 0.4 });
  const rotateY = useSpring(ry, { stiffness: 120, damping: 14, mass: 0.4 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    ry.set(nx * 9);
    rx.set(-ny * 9);
  };
  const resetTilt = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Subtle grid */}
      <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Top metadata bar */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 md:mb-20"
        >
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-forest-accent inline-block" />
            <span className="overline text-forest-accent">Specialty Antioxidants · Clean Chemistry · Est. 1995</span>
          </div>
          <div className="font-mono text-[11px] tracking-widest uppercase text-ink-soft">
            India / Global Distribution
          </div>
        </motion.div>

        <div className="grid grid-cols-12 gap-6 md:gap-8 items-end">
          {/* Headline — signature masked line-by-line reveal */}
          <div className="col-span-12 lg:col-span-8">
            <motion.h1
              style={{ y: headlineY }}
              className="font-display font-black tracking-tight leading-[0.92] text-ink text-balance"
            >
              <span
                className="block"
                style={{ fontSize: "clamp(2.75rem, 8vw, 7rem)" }}
              >
                <MaskedLine animateOnMount delay={0.15}>
                  The chemistry behind
                </MaskedLine>
                <MaskedLine
                  animateOnMount
                  delay={0.28}
                  innerClassName="italic font-light text-amber-accent"
                >
                  indispensable
                </MaskedLine>
                <MaskedLine animateOnMount delay={0.41}>
                  things.
                </MaskedLine>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-8 md:mt-10 max-w-xl text-ink-soft text-base md:text-lg leading-relaxed"
            >
              Milestone Preservatives operates three distinct product lines —{" "}
              <span className="text-amber-accent font-medium">
                Shelfanox™
              </span>{" "}
              synthetic antioxidants (TBHQ, BHA, BHT, AP),{" "}
              <span className="text-forest-accent font-medium">
                NatPro™
              </span>{" "}
              natural mixed tocopherols, and a{" "}
              <span className="text-steel-accent font-medium">
                Specialty Chemicals
              </span>{" "}
              range (MeHQ, Guaiacol) — engineered with scientific precision.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <Magnetic strength={0.25}>
                <a
                  href="#products"
                  data-testid="hero-cta-products"
                  className="group inline-flex items-center gap-2 bg-ink text-bone px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-ink/85 transition-all"
                >
                  Explore Products
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </Magnetic>
              <a
                href="#contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 border border-ink/20 text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-ink/5 transition-colors"
              >
                Request a Specification
              </a>
            </motion.div>
          </div>

          {/* Visual — 3D tilt frame with parallax image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-4 relative"
            style={{ perspective: 1000 }}
          >
            <motion.div
              onMouseMove={onTilt}
              onMouseLeave={resetTilt}
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative aspect-[4/5] w-full overflow-hidden rounded-sm border border-ink/10 bg-white"
            >
              <motion.img
                src={MEDIA.hero_bg}
                alt="Molecular structure dissolving into a natural leaf — abstract"
                style={{ y: imgY, scale: 1.16 }}
                className="w-full h-full object-cover will-change-transform"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-ink/5" />
            </motion.div>
            {/* Floating chip — three product lines */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: [0, -8, 0] }}
              transition={{
                opacity: { duration: 0.6, delay: 1.1 },
                y: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1.1 },
              }}
              className="hidden md:flex absolute -bottom-6 -left-8 bg-ink text-bone divide-x divide-white/15"
            >
              <div className="px-5 py-4">
                <div className="overline text-amber-accent mb-1.5">Shelfanox™</div>
                <div className="font-display text-sm leading-tight whitespace-nowrap">
                  Synthetic Antioxidants
                </div>
              </div>
              <div className="px-5 py-4">
                <div className="overline text-forest-accent mb-1.5">NatPro™</div>
                <div className="font-display text-sm leading-tight whitespace-nowrap">
                  Mixed Tocopherols
                </div>
              </div>
              <div className="px-5 py-4">
                <div className="overline text-steel-accent mb-1.5">Specialty</div>
                <div className="font-display text-sm leading-tight whitespace-nowrap">
                  MeHQ · Guaiacol
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-20 md:mt-28 flex items-center gap-3 text-ink-soft"
        >
          <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
          <span className="font-mono text-[11px] tracking-[0.25em] uppercase">
            Scroll · Synthetic & Natural Antioxidants
          </span>
        </motion.div>
      </div>
    </section>
  );
};
